import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import {
  getCompaniesQueryKey,
  listCompanies,
  type CompanyListItem,
} from '../../companies/api/companies.client.js';
import {
  getContactsQueryKey,
  listContacts,
  type ContactItem,
} from '../../contacts/api/contacts.client.js';
import { getDealsQueryKey, listDeals, type DealListItem } from '../../deals/api/deals.client.js';
import {
  getProjectsQueryKey,
  listProjects,
  type ProjectItem,
} from '../../projects/api/projects.client.js';
import { getTasksQueryKey, listTasks, type TaskItem } from '../../tasks/api/tasks.client.js';

/**
 * Debounced entity search for the global command menu (К7).
 *
 * Data layer only: the hook reuses the existing feature API clients and the
 * shared `entityListKey` factories, so search results live in the same
 * react-query cache prefix as the entity lists and are invalidated together
 * with them after a quick-create. Presentation (i18n subtitles, routes) stays
 * in the component.
 *
 * Server-side `q` exists for deals, contacts, tasks and projects. Companies
 * have no REST search — `listCompanies()` returns the whole (small) list, so
 * the hook filters it client-side over the same cached query.
 */

/** Keystrokes collapse into one request per typing pause. */
export const COMMAND_SEARCH_DEBOUNCE_MS = 250;

/** Shorter queries would match almost everything and help nobody. */
const COMMAND_SEARCH_MIN_LENGTH = 2;

/** Command palette shows a handful of hits per entity, not a data table. */
const COMMAND_SEARCH_MAX_PER_ENTITY = 5;

export interface CommandSearchData {
  deals: DealListItem[];
  contacts: ContactItem[];
  companies: CompanyListItem[];
  tasks: TaskItem[];
  projects: ProjectItem[];
}

export interface UseCommandSearchResult {
  /** Debounced trimmed query actually sent to the API. */
  query: string;
  data: CommandSearchData;
  isSearching: boolean;
}

export function useCommandSearch(rawQuery: string): UseCommandSearchResult {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';

  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(rawQuery.trim()), COMMAND_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [rawQuery]);

  const active = debouncedQuery.length >= COMMAND_SEARCH_MIN_LENGTH;
  const enabled = active && scope !== null;

  const dealsQuery = useQuery({
    queryKey: getDealsQueryKey(workspaceId, {
      q: debouncedQuery,
      limit: COMMAND_SEARCH_MAX_PER_ENTITY,
    }),
    queryFn: () => listDeals({ q: debouncedQuery, limit: COMMAND_SEARCH_MAX_PER_ENTITY }),
    enabled,
    retry: false,
  });

  const contactsQuery = useQuery({
    queryKey: getContactsQueryKey(workspaceId, { q: debouncedQuery }),
    queryFn: () => listContacts({ q: debouncedQuery }),
    enabled,
    retry: false,
  });

  const tasksQuery = useQuery({
    queryKey: getTasksQueryKey(workspaceId, { q: debouncedQuery }),
    queryFn: () => listTasks({ q: debouncedQuery }),
    enabled,
    retry: false,
  });

  const projectsQuery = useQuery({
    queryKey: getProjectsQueryKey(workspaceId, {
      q: debouncedQuery,
      limit: COMMAND_SEARCH_MAX_PER_ENTITY,
    }),
    queryFn: () => listProjects({ q: debouncedQuery, limit: COMMAND_SEARCH_MAX_PER_ENTITY }),
    enabled,
    retry: false,
  });

  const companiesQuery = useQuery({
    queryKey: getCompaniesQueryKey(workspaceId),
    queryFn: () => listCompanies(),
    enabled,
    staleTime: 60_000,
    retry: false,
  });

  const companies = useMemo(() => {
    if (!active) return [];
    const q = debouncedQuery.toLowerCase();
    return (companiesQuery.data ?? [])
      .filter(
        (company) =>
          company.name.toLowerCase().includes(q) ||
          (company.industry ?? '').toLowerCase().includes(q),
      )
      .slice(0, COMMAND_SEARCH_MAX_PER_ENTITY);
  }, [active, debouncedQuery, companiesQuery.data]);

  const isSearching =
    enabled &&
    (dealsQuery.isFetching ||
      contactsQuery.isFetching ||
      tasksQuery.isFetching ||
      projectsQuery.isFetching ||
      companiesQuery.isFetching);

  return {
    query: debouncedQuery,
    data: {
      deals: (dealsQuery.data ?? []).slice(0, COMMAND_SEARCH_MAX_PER_ENTITY),
      contacts: (contactsQuery.data ?? []).slice(0, COMMAND_SEARCH_MAX_PER_ENTITY),
      companies,
      tasks: (tasksQuery.data ?? []).slice(0, COMMAND_SEARCH_MAX_PER_ENTITY),
      projects: (projectsQuery.data ?? []).slice(0, COMMAND_SEARCH_MAX_PER_ENTITY),
    },
    isSearching,
  };
}
